import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertCircle,
  BarChart3,
  Camera,
  CheckCircle2,
  RotateCcw,
  Send,
  Trash2,
} from "lucide-react";
import { Loader } from "@/components/ui/Loader";
import { Modal } from "@/components/ui/Modal";
import { ConfirmModal } from "@/components/ui/ConfirmModal";
import { Button } from "@/components/ui/Button";
import {
  CHAO_MAX_PHOTOS_PER_AMBIENTE,
  chaoPhotoFileUrl,
  approveChaoReport,
  deleteChaoPhoto,
  rejectChaoReport,
  reportIsPending,
  submitChaoReportForApproval,
  fetchChaoReportDetail,
  photoCanDelete,
  saveChaoReportDraftProgress,
  uploadChaoPhoto,
  type ChaoPhotoDto,
  type ChaoReportSummaryDto,
  type ChaoServiceDef,
} from "@/services/chaoDeObra";
import { formatDateTime } from "@/utils/format";
import { cn } from "@/utils/cn";
import { useAppAccess } from "@/hooks/useAppAccess";
import { CHAO_AMBIENTES_UI, listMissingAmbientesForClose, matchAmbienteKey } from "./ambientes";
import { ChaoReportCloseRuleModal } from "./ChaoReportCloseRuleModal";
import { ChaoReportReturnModal } from "./ChaoReportReturnModal";
import { EapServiceProgressPanel } from "./EapServiceProgressPanel";

export type ChaoReportWorkTab = "photos" | "progress";

interface Props {
  open: boolean;
  onClose: () => void;
  unitId: string;
  reportId: string | null;
  unitLabel?: string;
  initialTab?: ChaoReportWorkTab;
  onClosed?: () => void;
  onSubmitted?: () => void;
  onApproved?: () => void;
  onReturned?: () => void;
  onUpdated?: () => void;
}

export function ChaoReportWorkModal({
  open,
  onClose,
  unitId,
  reportId,
  unitLabel,
  initialTab = "photos",
  onClosed,
  onSubmitted,
  onApproved,
  onReturned,
  onUpdated,
}: Props) {
  const { can } = useAppAccess();
  const canReview = can("chaoDeObraAprovar");
  const [tab, setTab] = useState<ChaoReportWorkTab>(initialTab);
  const [report, setReport] = useState<ChaoReportSummaryDto | null>(null);
  const [photos, setPhotos] = useState<ChaoPhotoDto[]>([]);
  const [catalog, setCatalog] = useState<ChaoServiceDef[]>([]);
  const [pctMap, setPctMap] = useState<Record<string, number>>({});
  const [minPctMap, setMinPctMap] = useState<Record<string, number>>({});
  const [generalPct, setGeneralPct] = useState(0);
  const [readOnly, setReadOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sessionBusy, setSessionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [okMsg, setOkMsg] = useState<string | null>(null);
  const [uploadAmbiente, setUploadAmbiente] = useState<string | null>(null);
  const [, setDeletingPhotoId] = useState<string | null>(null);
  const [closeRuleMissing, setCloseRuleMissing] = useState<string[] | null>(null);
  const [returnOpen, setReturnOpen] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<
    | { kind: "submit" }
    | { kind: "approve" }
    | { kind: "deletePhoto"; photo: ChaoPhotoDto }
    | null
  >(null);
  const draftTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (open) setTab(initialTab);
  }, [open, initialTab, reportId]);

  const load = useCallback(async () => {
    if (!unitId || !reportId) return;
    setLoading(true);
    setError(null);
    try {
      const detail = await fetchChaoReportDetail(unitId, reportId);
      if (!detail.success) {
        setError("Relatório não encontrado.");
        return;
      }
      setReport(detail.report);
      setPhotos(detail.photos ?? []);
      setCatalog(detail.serviceCatalog ?? []);
      setPctMap(detail.serviceProgress ?? {});
      setMinPctMap(detail.progressFloor ?? {});
      setGeneralPct(Number(detail.generalPct) || 0);
      setReadOnly(Boolean(detail.readOnly));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erro ao carregar relatório.");
    } finally {
      setLoading(false);
    }
  }, [unitId, reportId]);

  useEffect(() => {
    if (!open || !reportId) {
      setReport(null);
      setPhotos([]);
      setError(null);
      setOkMsg(null);
      setReturnOpen(false);
      setPendingConfirm(null);
      return;
    }
    void load();
  }, [open, reportId, load]);

  const persistDraft = useCallback(
    (next: Record<string, number>) => {
      if (!unitId || !reportId || readOnly) return;
      if (draftTimer.current) clearTimeout(draftTimer.current);
      draftTimer.current = setTimeout(() => {
        void saveChaoReportDraftProgress(unitId, reportId, next).then((res) => {
          if (res.success && res.generalPct != null) setGeneralPct(res.generalPct);
        });
      }, 400);
    },
    [unitId, reportId, readOnly],
  );

  const onPctChange = (id: string, value: number) => {
    const floor = minPctMap[id] ?? 0;
    const v = Math.max(floor, value);
    setPctMap((prev) => {
      const next = { ...prev, [id]: v };
      persistDraft(next);
      return next;
    });
  };

  const { photosByAmbiente } = useMemo(() => {
    const m = new Map<string, ChaoPhotoDto[]>();
    for (const p of photos) {
      const canon = matchAmbienteKey(p.ambiente ?? "");
      if (canon) {
        if (!m.has(canon)) m.set(canon, []);
        m.get(canon)!.push(p);
      }
    }
    for (const [key, arr] of m.entries()) {
      arr.sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt));
      m.set(key, arr.slice(0, CHAO_MAX_PHOTOS_PER_AMBIENTE));
    }
    return { photosByAmbiente: m };
  }, [photos]);

  const onPickPhoto = async (ambiente: string, fileList: FileList | null) => {
    if (!unitId || !fileList?.[0] || readOnly) return;
    const file = fileList[0];
    setUploadAmbiente(ambiente);
    setOkMsg(null);
    setError(null);
    try {
      const res = await uploadChaoPhoto(unitId, file, ambiente, "");
      if (!res.success) {
        setError(res.error ?? "Falha no envio da foto.");
        return;
      }
      setOkMsg("Foto registrada.");
      await load();
      onUpdated?.();
    } finally {
      setUploadAmbiente(null);
    }
  };

  const onDeletePhoto = (photo: ChaoPhotoDto) => {
    if (!photoCanDelete(photo)) return;
    setPendingConfirm({ kind: "deletePhoto", photo });
  };

  const runDeletePhoto = async (photo: ChaoPhotoDto) => {
    setDeletingPhotoId(photo.id);
    try {
      const res = await deleteChaoPhoto(photo.id);
      if (!res.success) setError(res.error ?? "Falha ao excluir.");
      else {
        setOkMsg("Foto excluída.");
        await load();
        onUpdated?.();
      }
    } finally {
      setDeletingPhotoId(null);
      setPendingConfirm(null);
    }
  };

  const pending = report ? reportIsPending(report) : false;

  const onSubmitForApproval = async () => {
    if (!unitId || !reportId || readOnly) return;
    const missing = listMissingAmbientesForClose(photos);
    if (missing.length > 0) {
      setCloseRuleMissing(missing);
      setTab("photos");
      return;
    }
    setPendingConfirm({ kind: "submit" });
  };

  const runSubmitForApproval = async () => {
    if (!unitId || !reportId || readOnly) return;
    setSessionBusy(true);
    setError(null);
    try {
      const res = await submitChaoReportForApproval(unitId, reportId, pctMap);
      if (!res.success) {
        if (res.missingAmbientes?.length) {
          setCloseRuleMissing(res.missingAmbientes);
          setTab("photos");
          return;
        }
        setError(res.error ?? "Não foi possível enviar o registro.");
        return;
      }
      onSubmitted?.();
      onClose();
    } finally {
      setSessionBusy(false);
      setPendingConfirm(null);
    }
  };

  const onApprove = () => {
    if (!unitId || !reportId || !canReview) return;
    setPendingConfirm({ kind: "approve" });
  };

  const runApprove = async () => {
    if (!unitId || !reportId || !canReview) return;
    setSessionBusy(true);
    setError(null);
    try {
      const res = await approveChaoReport(unitId, reportId);
      if (!res.success) {
        if (res.missingAmbientes?.length) {
          setCloseRuleMissing(res.missingAmbientes);
          setTab("photos");
          return;
        }
        setError(res.error ?? "Não foi possível aprovar o registro.");
        return;
      }
      onApproved?.();
      onClosed?.();
      onClose();
    } finally {
      setSessionBusy(false);
      setPendingConfirm(null);
    }
  };

  const onReject = () => {
    if (!unitId || !reportId || !canReview) return;
    setReturnOpen(true);
  };

  const runReject = async (note: string) => {
    if (!unitId || !reportId || !canReview) return;
    setSessionBusy(true);
    setError(null);
    try {
      const res = await rejectChaoReport(unitId, reportId, note);
      if (!res.success) {
        throw new Error(res.error ?? "Não foi possível devolver o registro.");
      }
      setReturnOpen(false);
      onReturned?.();
      await load();
    } finally {
      setSessionBusy(false);
    }
  };

  const modalTitle = report
    ? pending
      ? `Aguardando aprovação — ${formatDateTime(report.at)}`
      : report.isOpen
        ? `Registro em andamento — ${formatDateTime(report.startedAt)}`
        : `Registro concluído — ${formatDateTime(report.at)}`
    : "Relatório";

  const tabs: { id: ChaoReportWorkTab; label: string; icon: typeof Camera }[] = [
    { id: "photos", label: "Foto por Ambiente", icon: Camera },
    { id: "progress", label: "Avanço Físico", icon: BarChart3 },
  ];

  return (
    <>
      <Modal
        open={open && Boolean(reportId)}
        onClose={onClose}
        size="full"
        title={
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-slate-800 leading-tight truncate">{modalTitle}</h2>
            {unitLabel && <p className="text-xs text-slate-500 mt-0.5 truncate">{unitLabel}</p>}
          </div>
        }
        footer={
          report && !readOnly ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-xs text-slate-500 sm:flex-1">
                Envie ao menos uma foto em cada ambiente e registre o avanço antes de enviar para aprovação.
              </p>
              <Button
                variant="primary"
                leftIcon={<Send className="w-4 h-4" />}
                onClick={() => void onSubmitForApproval()}
                loading={sessionBusy}
              >
                Enviar para aprovação
              </Button>
            </div>
          ) : report && pending ? (
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <p className="text-xs text-slate-500 sm:flex-1">
                {canReview
                  ? "Avalie fotos e medições. Só após a aprovação o avanço entra na obra."
                  : "Registro enviado. Aguarde a avaliação de um administrador. O avanço ainda não foi computado."}
              </p>
              {canReview ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    leftIcon={<RotateCcw className="w-4 h-4" />}
                    onClick={() => void onReject()}
                    loading={sessionBusy}
                  >
                    Devolver
                  </Button>
                  <Button
                    variant="primary"
                    leftIcon={<CheckCircle2 className="w-4 h-4" />}
                    onClick={() => void onApprove()}
                    loading={sessionBusy}
                  >
                    Aprovar
                  </Button>
                </div>
              ) : null}
            </div>
          ) : undefined
        }
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
            <Loader size="lg" label="Carregando relatório…" />
          </div>
        ) : (
          <>
            <div className="flex flex-col gap-4 -mt-1">
              <div className="flex gap-1 p-1 rounded-xl bg-slate-100 border border-slate-200 w-full sm:w-auto">
                {tabs.map(({ id, label, icon: Icon }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setTab(id)}
                    className={cn(
                      "flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-colors",
                      tab === id
                        ? "bg-white text-brand-700 shadow-sm ring-1 ring-slate-200"
                        : "text-slate-600 hover:text-slate-800",
                    )}
                  >
                    <Icon className="w-4 h-4 flex-shrink-0" />
                    {label}
                  </button>
                ))}
              </div>

              {error && (
                <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  {error}
                </div>
              )}
              {okMsg && (
                <div className="px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-xl text-emerald-800 text-sm font-medium">
                  {okMsg}
                </div>
              )}

              {tab === "photos" && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4 pb-2">
                  {CHAO_AMBIENTES_UI.map((a) => {
                    const Icon = a.icon;
                    const busy = uploadAmbiente === a.key;
                    const ambPhotos = photosByAmbiente.get(a.key) ?? [];
                    return (
                      <div
                        key={a.key}
                        className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm flex flex-col gap-3"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={cn("w-12 h-12 rounded-xl flex items-center justify-center text-white", a.bar)}
                          >
                            <Icon className="w-6 h-6" />
                          </div>
                          <p className="font-bold text-slate-900">{a.label}</p>
                        </div>
                        {ambPhotos.length > 0 ? (
                          <div className="flex gap-2 overflow-x-auto">
                            {ambPhotos.map((p) => (
                              <div
                                key={p.id}
                                className="relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border"
                              >
                                <img src={chaoPhotoFileUrl(p.id)} alt="" className="w-full h-full object-cover" />
                                {photoCanDelete(p) && (
                                  <button
                                    type="button"
                                    onClick={() => void onDeletePhoto(p)}
                                    className="absolute top-0.5 right-0.5 p-1 rounded bg-black/55 text-white"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-[11px] text-slate-400 text-center py-2 border border-dashed rounded-lg">
                            Sem foto
                          </p>
                        )}
                        {!readOnly && (
                          <label className="block">
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              className="sr-only"
                              disabled={busy}
                              onChange={(e) => {
                                void onPickPhoto(a.key, e.target.files);
                                e.target.value = "";
                              }}
                            />
                            <span
                              className={cn(
                                "flex justify-center py-2 rounded-xl text-xs font-semibold border-2 border-dashed cursor-pointer",
                                busy
                                  ? "border-slate-200 text-slate-400"
                                  : "border-brand-200 text-brand-700 hover:bg-brand-50",
                              )}
                            >
                              {busy ? "Enviando…" : "Registrar foto"}
                            </span>
                          </label>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {tab === "progress" && (
                <div className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4 sm:p-5">
                  <EapServiceProgressPanel
                    catalog={catalog}
                    pctMap={pctMap}
                    minPctMap={minPctMap}
                    generalPct={generalPct}
                    unitId={unitId}
                    readOnly={readOnly}
                    hideSaveButton
                    onPctChange={onPctChange}
                    onWeightsSaved={(cat, pct) => {
                      setCatalog(cat);
                      setGeneralPct(pct);
                    }}
                    lastUpdateBy={report?.responsavel}
                  />
                </div>
              )}
            </div>
          </>
        )}
      </Modal>

      <ChaoReportCloseRuleModal
        open={closeRuleMissing !== null}
        onClose={() => setCloseRuleMissing(null)}
        missingAmbientes={closeRuleMissing ?? []}
      />

      <ChaoReportReturnModal
        open={returnOpen}
        onClose={() => {
          if (!sessionBusy) setReturnOpen(false);
        }}
        onConfirm={runReject}
        loading={sessionBusy}
      />

      <ConfirmModal
        isOpen={pendingConfirm !== null}
        title={
          pendingConfirm?.kind === "deletePhoto"
            ? "Excluir foto"
            : pendingConfirm?.kind === "approve"
              ? "Aprovar registro"
              : "Enviar para aprovação"
        }
        message={
          pendingConfirm?.kind === "deletePhoto"
            ? "Excluir esta foto permanentemente?"
            : pendingConfirm?.kind === "approve"
              ? "Aprovar este registro? O avanço informado será computado na medição da unidade e não poderá ser reduzido no próximo relatório."
              : "Enviar este registro para aprovação? O avanço só será computado depois que um administrador avaliar."
        }
        confirmLabel={
          pendingConfirm?.kind === "deletePhoto"
            ? "Excluir"
            : pendingConfirm?.kind === "approve"
              ? "Aprovar"
              : "Enviar"
        }
        confirmVariant={pendingConfirm?.kind === "deletePhoto" ? "danger" : "primary"}
        loading={sessionBusy}
        onCancel={() => {
          if (!sessionBusy) setPendingConfirm(null);
        }}
        onConfirm={async () => {
          if (pendingConfirm?.kind === "deletePhoto") {
            await runDeletePhoto(pendingConfirm.photo);
            return;
          }
          if (pendingConfirm?.kind === "approve") {
            await runApprove();
            return;
          }
          await runSubmitForApproval();
        }}
      />
    </>
  );
}
